import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPosts } from "state";
import {
  EditOutlined,
  DeleteOutlined,
  AttachFileOutlined,
  GifBoxOutlined,
  ImageOutlined,
  MicOutlined,
  MoreHorizOutlined,
  SentimentSatisfiedOutlined,
  VideocamOutlined,
} from "@mui/icons-material";
import {
  useTheme,
  useMediaQuery,
  Box,
  Divider,
  Typography,
  InputBase,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import api from 'state/axios';
import FlexBetween from "components/FlexBetween";
import Dropzone from "react-dropzone";
import UserImage from "components/UserImage";
import WidgetWrapper from "components/WidgetWrapper";

const MyPostWidget = ({ picturePath }) => {
  const dispatch = useDispatch();
  const { _id } = useSelector((state) => state.user);
  const theme = useTheme();
  const neutralDark = theme.palette.neutral.dark;
  const neutralDeep = theme.palette.neutral.deep;
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [isExpanded, setIsExpanded] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [image, setImage] = useState(null);
  const [post, setPost] = useState("");

  const handlePost = async () => {
    const formData = new FormData();
    formData.append("userId", _id);
    formData.append("description", post);
    if (image) {
      formData.append("picture", image);
      formData.append("picturePath", image.name);
    }

    const response = await api.post('/posts', formData, {
      skipAuth: true
    });
    const posts = response.data;

    if (Array.isArray(posts)) {
      dispatch(setPosts({ posts }));
      setImage(null);
      setPost("");
      setIsImage(false);
      setIsExpanded(false);
    }
  };

  const handleCancel = () => {
    setIsExpanded(false);
    setIsImage(false);
    setImage(null);
    setPost("");
  };

  return (
    <WidgetWrapper>
      {/* Collapsed trigger */}
      {!isExpanded ? (
        <FlexBetween gap={1.5} onClick={() => setIsExpanded(true)} sx={{ cursor: "pointer" }}>
          <UserImage image={picturePath} size="42px" />
          <Box
            sx={{
              flex: 1,
              backgroundColor: theme.palette.neutral.light,
              borderRadius: "2rem",
              px: 2,
              py: 1,
              color: theme.palette.neutral.deep,
              fontSize: "0.95rem",
              transition: "background 0.15s",
              "&:hover": {
                backgroundColor: theme.palette.neutral.mid,
                color: theme.palette.neutral.dark,
              },
            }}
          >
            What's on your mind?
          </Box>
        </FlexBetween>
      ) : (
        <>
          {/* Expanded: avatar + textarea */}
          <FlexBetween gap={1.5} alignItems="flex-start">
            <UserImage image={picturePath} size="42px" />
            <InputBase
              placeholder="What's on your mind?"
              onChange={(e) => setPost(e.target.value)}
              value={post}
              multiline
              minRows={2}
              autoFocus
              sx={{
                flex: 1,
                backgroundColor: theme.palette.neutral.light,
                borderRadius: "0.75rem",
                padding: "0.75rem 1rem",
                fontSize: "0.95rem",
                alignItems: "flex-start",
              }}
            />
          </FlexBetween>

          {/* Image dropzone */}
          {isImage && (
            <Box
              border={`1px solid ${neutralDeep}`}
              borderRadius="0.75rem"
              mt="1rem"
              p="1rem"
            >
              <Dropzone
                acceptedFiles=".jpg,.jpeg,.png"
                multiple={false}
                onDrop={(acceptedFiles) => setImage(acceptedFiles[0])}
              >
                {({ getRootProps, getInputProps }) => (
                  <FlexBetween>
                    <Box
                      {...getRootProps()}
                      border={`2px dashed ${theme.palette.primary.main}`}
                      p="1rem"
                      width="100%"
                      borderRadius="0.5rem"
                      sx={{
                        "&:hover": {
                          cursor: "pointer",
                          borderColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      {!image ? (
                        <Typography color={neutralDeep} textAlign="center" fontSize="0.9rem">
                          Click or drag an image here
                        </Typography>
                      ) : (
                        <FlexBetween gap="0.75rem">
                          <Box display="flex" alignItems="center" gap="0.75rem">
                            <img
                              src={URL.createObjectURL(image)}
                              alt="preview"
                              style={{
                                width: 56,
                                height: 56,
                                objectFit: "cover",
                                borderRadius: "0.375rem",
                              }}
                            />
                            <Typography variant="body2" color={neutralDark}>
                              {image.name}
                            </Typography>
                          </Box>
                          <EditOutlined sx={{ color: neutralDeep }} />
                        </FlexBetween>
                      )}
                    </Box>
                    {image && (
                      <IconButton onClick={() => setImage(null)} sx={{ ml: 1 }}>
                        <DeleteOutlined />
                      </IconButton>
                    )}
                  </FlexBetween>
                )}
              </Dropzone>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Action bar */}
          <FlexBetween>
            <Box display="flex" gap={0.25}>
              <Tooltip title="Photo">
                <IconButton size="small" onClick={() => setIsImage(!isImage)}>
                  <ImageOutlined
                    sx={{ color: isImage ? theme.palette.primary.main : neutralDark, fontSize: "1.2rem" }}
                  />
                </IconButton>
              </Tooltip>

              {isDesktop ? (
                <>
                  <Tooltip title="Video">
                    <IconButton size="small">
                      <VideocamOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="GIF">
                    <IconButton size="small">
                      <GifBoxOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Attachment">
                    <IconButton size="small">
                      <AttachFileOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Audio">
                    <IconButton size="small">
                      <MicOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Feeling">
                    <IconButton size="small">
                      <SentimentSatisfiedOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <IconButton size="small">
                  <MoreHorizOutlined sx={{ color: neutralDark, fontSize: "1.2rem" }} />
                </IconButton>
              )}
            </Box>

            <Box display="flex" gap={1}>
              <Button
                onClick={handleCancel}
                sx={{
                  color: neutralDeep,
                  borderRadius: "2rem",
                  textTransform: "none",
                  fontSize: "0.85rem",
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!post.trim()}
                onClick={handlePost}
                variant="contained"
                sx={{
                  borderRadius: "2rem",
                  textTransform: "none",
                  fontSize: "0.85rem",
                  px: 2.5,
                }}
              >
                Post
              </Button>
            </Box>
          </FlexBetween>
        </>
      )}
    </WidgetWrapper>
  );
};

export default MyPostWidget;
